import {
  useQuery as useReactQuery,
  useInfiniteQuery as useReactInfiniteQuery,
  useMutation as useReactMutation,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  InfiniteData,
  QueryFunction,
  QueryKey,
} from '@tanstack/react-query'
import { ResolveNotification, useResolvedPromise } from './useResolvedPromise'

/**
 * useQuery is a hook for fetching and caching data using the QueryClient on context.
 *
 * This is a thin wrapper around https://react-query.tanstack.com/reference/useQuery that also
 * invokes `useResolvedPromise` for things like error handling.
 */
export const useQuery = <TQueryFnData>(
  key: QueryKey,
  fn: () => Promise<TQueryFnData>,
  options?: Omit<UseQueryOptions<TQueryFnData>, 'queryKey' | 'queryFn'>,
  notification?: ResolveNotification,
) => {
  const result = useReactQuery<TQueryFnData>({ queryKey: key, queryFn: fn, ...options })

  useResolvedPromise(
    {
      result: result.data,
      loading: result.isPending,
      error: result.error,
    },
    notification,
  )

  return result
}

/**
 * useInfiniteQuery is a hook for progressively fetching and caching data using the QueryClient on context.
 *
 * This is a thin wrapper around https://react-query.tanstack.com/reference/useInfiniteQuery that also
 * invokes `useResolvedPromise` for things like error handling.
 */
export const useInfiniteQuery = <TQueryFnData, TPageParam, TQueryKey extends QueryKey = QueryKey>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey, TPageParam>,
  options: Omit<
    UseInfiniteQueryOptions<
      TQueryFnData,
      Error,
      InfiniteData<TQueryFnData, TPageParam>,
      TQueryKey,
      TPageParam
    >,
    'queryKey' | 'queryFn'
  >,
  notification?: ResolveNotification,
) => {
  const result = useReactInfiniteQuery({ queryKey: key, queryFn: fn, ...options })

  useResolvedPromise(
    {
      result: result.data,
      loading: result.isPending,
      error: result.error,
    },
    notification,
  )

  return result
}

/**
 * useMutation is a hook for invoking mutative API operations using the QueryClient on context.
 *
 * This is a thin wrapper around https://react-query.tanstack.com/reference/useMutation that also
 * invokes `useResolvedPromise` for things like error handling.
 */
export const useMutation = <TData, TError = unknown, TVariables = void, TContext = unknown>(
  fn: (variables: TVariables) => Promise<TData>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>,
  notification?: ResolveNotification,
) => {
  const result = useReactMutation<TData, TError, TVariables, TContext>({
    mutationFn: fn,
    ...options,
  })

  useResolvedPromise(
    {
      result: result.data,
      loading: result.isPending,
      error: result.error,
    },
    notification,
  )

  return result
}
